import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { astrologyEndPoint } from '../../../Services/AllApi';
import { apiConnector } from '../../../Services/ApiConnector';
import { FaSpinner } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useLanguage } from '../../../i18n/LanguageContext';

const AdvancedHoroscopeFeature = () => {
  const { t } = useLanguage();
  const [selectedSign, setSelectedSign] = useState('aries');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [loading, setLoading] = useState(false);

  const { DAILY_HOROSCOPE_ADVANCED_API } = astrologyEndPoint;

  const zodiacSigns = [
    { value: 'aries', label: t('aries'), icon: '♈' },
    { value: 'taurus', label: t('taurus'), icon: '♉' },
    { value: 'gemini', label: t('gemini'), icon: '♊' },
    { value: 'cancer', label: t('cancer'), icon: '♋' },
    { value: 'leo', label: t('leo'), icon: '♌' },
    { value: 'virgo', label: t('virgo'), icon: '♍' },
    { value: 'libra', label: t('libra'), icon: '♎' },
    { value: 'scorpio', label: t('scorpio'), icon: '♏' },
    { value: 'sagittarius', label: t('sagittarius'), icon: '♐' },
    { value: 'capricorn', label: t('capricorn'), icon: '♑' },
    { value: 'aquarius', label: t('aquarius'), icon: '♒' },
    { value: 'pisces', label: t('pisces'), icon: '♓' }
  ];

  const handleGetHoroscope = async () => {
    setLoading(true);
    setHoroscopeData(null);
    
    try {
      const dateTime = new Date(selectedDate + 'T10:50:40+00:00').toISOString();
      
      // Get language from localStorage or default to 'en'
      const currentLanguage = localStorage.getItem('language') || 'hi';
      
      const response = await apiConnector(
        'GET',
        `${DAILY_HOROSCOPE_ADVANCED_API}?datetime=${encodeURIComponent(dateTime)}&sign=${selectedSign}&type=all&la=${currentLanguage}`,
        null,
        null
      );
      
      if (response?.data) {
        setHoroscopeData(response.data); // ✅ FIXED
        toast.success(t('kundliGeneratedSuccessfully'));
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching advanced horoscope:', error);
      
      // Provide more specific error messages
      if (error.response?.status === 503) {
        toast.error(t('serviceUnavailable'));
      } else if (error.response?.status === 404) {
        toast.error(t('serviceNotFound'));
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        toast.error('Network error. Please check your internet connection.');
      } else {
        toast.error(`${t('requestFailed')}: ${error.response?.statusText || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div 
      id="daily-horoscope"
      className="bg-white rounded-2xl shadow-lg p-8 mb-8"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      whileHover={{ scale: 1.02, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
    >
      <motion.h3 
        className="text-2xl font-bold text-orange-800 mb-6 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        >
          {t('dailyHoroscopeAPI')}
        </motion.h3>
      
      {/* Date + Sign Selectors */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">{t('selectDate')}</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">{t('selectZodiacSign')}</label>
          <select
            value={selectedSign}
            onChange={(e) => setSelectedSign(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
          >
            {zodiacSigns.map((sign) => (
              <option key={sign.value} value={sign.value}>
                {sign.icon} {sign.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Button */}
      <motion.div 
        className="text-center mb-6"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <motion.button
          onClick={handleGetHoroscope}
          disabled={loading}
          className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-3 rounded-full font-semibold hover:from-orange-600 hover:to-red-600 transition-all disabled:opacity-50"
          whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}
          whileTap={{ scale: 0.95 }}
        >
            {loading ? (
              <>
                <FaSpinner className="animate-spin inline mr-2" /> {t('generating')}...
              </>
            ) : (
              t('getDailyHoroscope')
            )}
        </motion.button>
      </motion.div>

      {/* Results */}
      {horoscopeData?.data?.daily_predictions && horoscopeData.data.daily_predictions[0] && (
        <motion.div 
          className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-200"
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-xl font-bold text-orange-800">
              {zodiacSigns.find((s) => s.value === selectedSign)?.icon}{' '}
              {zodiacSigns.find((s) => s.value === selectedSign)?.label}
            </h4>
            <span className="text-sm text-gray-600">
              {new Date(selectedDate).toLocaleDateString()}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Card 1: Sign Info */}
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Sign Info</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-600">Name:</span>
                  <span className="text-gray-800">{horoscopeData.data.daily_predictions[0]?.sign?.name || 'N/A'}</span>
                  </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-600">Symbol:</span>
                  <span className="text-2xl">{horoscopeData.data.daily_predictions[0]?.sign_info?.unicode_symbol || '♈'}</span>
                  </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-600">Lord:</span>
                  <span className="text-gray-800">{horoscopeData.data.daily_predictions[0]?.sign?.lord?.name || 'N/A'}</span>
                  </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-600">Modality:</span>
                  <span className="text-gray-800">{horoscopeData.data.daily_predictions[0]?.sign_info?.modality || 'N/A'}</span>
                  </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-600">Triplicity:</span>
                  <span className="text-gray-800">{horoscopeData.data.daily_predictions[0]?.sign_info?.triplicity || 'N/A'}</span>
                  </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-600">Quadruplicity:</span>
                  <span className="text-gray-800">{horoscopeData.data.daily_predictions[0]?.sign_info?.quadruplicity || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Prediction Cards */}
            {horoscopeData.data.daily_predictions[0]?.predictions?.map((prediction, index) => {
              const getCardColors = (type) => {
                switch (type.toLowerCase()) {
                  case 'general':
                    return 'from-orange-50 to-amber-50 border-orange-200 text-orange-800';
                  case 'health':
                    return 'from-orange-100 to-amber-100 border-orange-300 text-orange-900';
                  case 'career':
                    return 'from-amber-50 to-orange-50 border-amber-200 text-amber-800';
                  case 'love':
                    return 'from-orange-50 to-red-50 border-orange-200 text-orange-800';
                  default:
                    return 'from-orange-50 to-amber-50 border-orange-200 text-orange-800';
                }
              };

              return (
                <div
                  key={index}
                  className={`bg-gradient-to-br ${getCardColors(prediction.type)} rounded-xl p-6 shadow-md border`}
                >
                  <h3 className="text-xl font-bold mb-4">{prediction.type}</h3>
                      <div className="space-y-3 text-sm">
                          <div>
                      <h4 className="font-semibold mb-1">Prediction:</h4>
                      <p className="leading-relaxed">{prediction.prediction}</p>
                          </div>
                          <div>
                      <h4 className="font-semibold mb-1">Seek:</h4>
                      <p className="leading-relaxed">{prediction.seek}</p>
                          </div>
                          <div>
                      <h4 className="font-semibold mb-1">Challenge:</h4>
                      <p className="leading-relaxed">{prediction.challenge}</p>
                          </div>
                          <div>
                      <h4 className="font-semibold mb-1">Insight:</h4>
                      <p className="leading-relaxed">{prediction.insight}</p>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Aspects Card */}
            {horoscopeData.data.daily_predictions[0]?.aspects?.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Aspects</h3>
                <div className="space-y-2">
                  {horoscopeData.data.daily_predictions[0].aspects.map((aspect, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3 text-sm">
                      <span className="font-medium">
                        {aspect.planet_one?.name || aspect.planet_one} — {aspect.aspect?.name || aspect.aspect} — {aspect.planet_two?.name || aspect.planet_two}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Transits Card */}
            {horoscopeData.data.daily_predictions[0]?.transits?.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Transits</h3>
                <div className="space-y-2">
                  {horoscopeData.data.daily_predictions[0].transits.map((transit, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{transit.planet?.name || transit.planet}</span>
                        {transit.is_retrograde && (
                          <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full font-medium">
                            Retrograde
                          </span>
                        )}
                      </div>
                      <div className="text-gray-600 mt-1">
                        {transit.zodiac?.name || transit.zodiac} - House {transit.house_number}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

    </motion.div>
  );
};

export default AdvancedHoroscopeFeature;
